"use client"

import type React from "react"
import OrgLayout from "@/layouts/OrgLayout"

import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Plug, Plus, Edit, Trash2, Webhook, Search } from "lucide-react"
import type { Integration } from "@/types"

export interface CustomIntegration {
	id: number
	name: string
	type: "webhook"
	url: string
	auth_type?: "api_key" | "http_basic" | "none"
	api_key?: string
	http_basic_username?: string
	http_basic_password?: string
	enabled: boolean
}



export interface IntegrationsProps {
	activeView?: "active-integrations" | "browse-integrations"
	integrations?: Integration[]
}


function Integrations({ integrations: initialIntegrations }: IntegrationsProps) {
	const [integrations, setIntegrations] = useState<Integration[]>(initialIntegrations || [])
	const [customIntegrationDialogOpen, setCustomIntegrationDialogOpen] = useState(false)
	const [editingCustomIntegration, setEditingCustomIntegration] = useState<CustomIntegration | null>(null)
	const [customIntegrations, setCustomIntegrations] = useState<CustomIntegration[]>([
		{
			id: 1,
			name: "Patient Portal Webhook",
			type: "webhook",
			url: "https://portal.example.com/webhook",
			auth_type: "api_key",
			api_key: "sk_test_123",
			enabled: true,
		},
	])

	const activeView = window.location.pathname.split("/").pop() || "active-integrations"
	const [authType, setAuthType] = useState<"api_key" | "http_basic" | "none">("none")
	const [learnMoreOpen, setLearnMoreOpen] = useState(false)
	const [selectedIntegration, setSelectedIntegration] = useState<(Integration) | null>(null)
	const [searchQuery, setSearchQuery] = useState("")

	const toggleIntegration = (id: number) => {
		setIntegrations(
			integrations.map((integration) =>
				integration.id === id ? { ...integration, enabled: !integration.enabled } : integration,
			),
		)
	}

	const handleSaveCustomIntegration = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const formData = new FormData(e.currentTarget)
		const name = formData.get("name") as string
		const url = formData.get("url") as string
		const auth_type = formData.get("auth_type") as "api_key" | "http_basic" | "none"
		const api_key = formData.get("api_key") as string
		const http_basic_username = formData.get("http_basic_username") as string
		const http_basic_password = formData.get("http_basic_password") as string

		if (editingCustomIntegration) {
			setCustomIntegrations(
				customIntegrations.map((i) =>
					i.id === editingCustomIntegration.id
						? { ...i, name, url, auth_type, api_key, http_basic_username, http_basic_password }
						: i,
				),
			)
		} else {
			setCustomIntegrations([
				...customIntegrations,
				{
					id: Date.now(),
					name,
					type: "webhook",
					url,
					auth_type,
					api_key,
					http_basic_username,
					http_basic_password,
					enabled: true,
				},
			])
		}
		setCustomIntegrationDialogOpen(false)
		setEditingCustomIntegration(null)
	}

	const activeIntegrations = integrations.filter((i) => i.enabled)

	const filteredCustomIntegrations = customIntegrations.filter(
		(integration) =>
			integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			integration.url.toLowerCase().includes(searchQuery.toLowerCase()),
	)

	const filteredActiveIntegrations = activeIntegrations.filter((integration) =>
		integration.name.toLowerCase().includes(searchQuery.toLowerCase()),
	)

	const filteredAvailableIntegrations = integrations.filter(
		(integration) =>
			integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
			integration.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			integration.category?.toLowerCase().includes(searchQuery.toLowerCase()),
	)

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-2xl font-semibold">
						{activeView === "browse-integrations" ? "Browse Integrations" : "Active Integrations"}
					</h2>
					<p className="text-sm text-muted-foreground">
						{activeView === "browse-integrations"
							? "Discover and add new integrations"
							: "Manage your active integrations"}
					</p>
				</div>
			</div>

			<div className="flex items-center gap-2">
				<Search className=" h-4 w-4  text-muted-foreground" />
				<Input
					placeholder={
						activeView === "browse-integrations" ? "Search available integrations..." : "Search active integrations..."
					}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					className="pl-9"
				/>
			</div>

			{activeView === "active-integrations" ? (
				<div className="space-y-6">
					<div className="flex items-center justify-between">
						<h3 className="text-lg font-medium">Custom Integrations</h3>
						<Dialog open={customIntegrationDialogOpen} onOpenChange={setCustomIntegrationDialogOpen}>
							<DialogTrigger asChild>
								<Button
									onClick={() => {
										setEditingCustomIntegration(null)
										setAuthType("none")
									}}
								>
									<Plus className="h-4 w-4 mr-2" />
									Add Custom Integration
								</Button>
							</DialogTrigger>
							<DialogContent>
								<form onSubmit={handleSaveCustomIntegration}>
									<DialogHeader>
										<DialogTitle>
											{editingCustomIntegration ? "Edit Custom Integration" : "Add Custom Integration"}
										</DialogTitle>
										<DialogDescription>Configure a custom webhook integration</DialogDescription>
									</DialogHeader>
									<div className="space-y-4 py-4">
										<div className="space-y-2">
											<Label htmlFor="integration-name">Integration Name</Label>
											<Input
												id="integration-name"
												name="name"
												defaultValue={editingCustomIntegration?.name}
												placeholder="Patient Portal Webhook"
												required
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="integration-url">Webhook URL</Label>
											<Input
												id="integration-url"
												name="url"
												defaultValue={editingCustomIntegration?.url}
												placeholder="https://api.example.com/webhook"
												required
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="auth-type">Authentication Type</Label>
											<Select
												name="auth_type"
												value={authType}
												onValueChange={(value) => setAuthType(value as "api_key" | "http_basic" | "none")}
												defaultValue={editingCustomIntegration?.auth_type || "none"}
											>
												<SelectTrigger>
													<SelectValue placeholder="Select authentication type" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">None</SelectItem>
													<SelectItem value="api_key">API Key</SelectItem>
													<SelectItem value="http_basic">HTTP Basic Auth</SelectItem>
												</SelectContent>
											</Select>
										</div>
										{authType === "api_key" && (
											<div className="space-y-2">
												<Label htmlFor="api-key">API Key</Label>
												<Input
													id="api-key"
													name="api_key"
													defaultValue={editingCustomIntegration?.api_key}
													placeholder="sk_test_..."
													required
												/>
											</div>
										)}
										{authType === "http_basic" && (
											<>
												<div className="space-y-2">
													<Label htmlFor="http-basic-username">Username</Label>
													<Input
														id="http-basic-username"
														name="http_basic_username"
														defaultValue={editingCustomIntegration?.http_basic_username}
														placeholder="username"
														required
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor="http-basic-password">Password</Label>
													<Input
														id="http-basic-password"
														name="http_basic_password"
														type="password"
														defaultValue={editingCustomIntegration?.http_basic_password}
														placeholder="password"
														required
													/>
												</div>
											</>
										)}
									</div>
									<DialogFooter>
										<Button type="submit">{editingCustomIntegration ? "Update" : "Create"}</Button>
									</DialogFooter>
								</form>
							</DialogContent>
						</Dialog>
					</div>

					{filteredCustomIntegrations.length > 0 ? (
						<div className="space-y-3">
							{filteredCustomIntegrations.map((integration) => (
								<Card key={integration.id} className="p-4">
									<div className="flex items-center justify-between">
										<div className="flex items-center gap-3">
											<Webhook className="h-5 w-5 text-muted-foreground" />
											<div>
												<p className="font-medium">{integration.name}</p>
												<p className="text-xs text-muted-foreground">{integration.url}</p>
												{integration.auth_type && integration.auth_type !== "none" && (
													<p className="text-xs text-muted-foreground mt-1">
														Auth: {integration.auth_type === "api_key" ? "API Key" : "HTTP Basic"}
													</p>
												)}
											</div>
										</div>
										<div className="flex gap-2">
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												onClick={() => {
													setEditingCustomIntegration(integration)
													setAuthType(integration.auth_type || "none")
													setCustomIntegrationDialogOpen(true)
												}}
											>
												<Edit className="h-3 w-3" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												onClick={() => setCustomIntegrations(customIntegrations.filter((i) => i.id !== integration.id))}
											>
												<Trash2 className="h-3 w-3 text-destructive" />
											</Button>
										</div>
									</div>
								</Card>
							))}
						</div>
					) : customIntegrations.length > 0 ? (
						<Card className="p-8 text-center">
							<Search className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
							<p className="text-sm text-muted-foreground">No custom integrations match your search</p>
						</Card>
					) : (
						<Card className="p-8 text-center">
							<Webhook className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
							<p className="text-sm text-muted-foreground">No custom integrations yet</p>
							<p className="text-xs text-muted-foreground mt-1">Click "Add Custom Integration" to create one</p>
						</Card>
					)}

					{filteredActiveIntegrations.length > 0 && (
						<div className="space-y-3">
							<h3 className="text-lg font-medium">Third-Party Integrations</h3>
							{filteredActiveIntegrations.map((integration) => (
								<Card key={integration.id} className="p-4">
									<div className="flex items-start justify-between">
										<div className="flex items-center gap-3">
											<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
												<Plug className="h-5 w-5 text-primary" />
											</div>
											<div>
												<h4 className="font-medium">{integration.name}</h4>
												<p className="text-xs text-muted-foreground">Third-party integration</p>
												<p className="text-xs text-green-600 mt-1">Active • Last synced 2 hours ago</p>
											</div>
										</div>
										<div className="flex items-center gap-3">
											<Label htmlFor={`integration-${integration.id}`} className="text-sm">
												Enabled
											</Label>
											<Switch
												id={`integration-${integration.id}`}
												checked={integration.enabled}
												onCheckedChange={() => toggleIntegration(integration.id)}
											/>
										</div>
									</div>
								</Card>
							))}
						</div>
					)}
				</div>
			) : (
				<div>
					<h3 className="text-lg font-medium mb-4">Available Integrations</h3>
					{filteredAvailableIntegrations.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{filteredAvailableIntegrations.map((integration) => {
								const isActive = integrations.find((i) => i.id === integration.id)?.enabled
								return (
									<Card key={integration.id} className="p-6 hover:border-primary/50 transition-colors">
										<div className="flex flex-col h-full">
											<div className="flex items-start gap-4 mb-4">
												<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 shrink-0">
													<img src={integration.icon_url} alt={`${integration.name} icon`} className="h-6 w-6" />
												</div>
												<div className="flex-1 min-w-0">
													<h4 className="font-semibold mb-1">{integration.name}</h4>
													<p className="text-xs text-muted-foreground">{integration.category}</p>
												</div>
											</div>
											<p className="text-sm text-muted-foreground mb-4 flex-1">{integration.description}</p>
											<div className="flex gap-2 mt-auto">
												<Button
													className="flex-1"
													variant={isActive ? "outline" : "default"}
													onClick={() => {
														if (!isActive) {
															toggleIntegration(integration.id)
														}
													}}
												>
													{isActive ? "Configure" : "Add Integration"}
												</Button>
												<Button
													variant="outline"
													className="flex-1 bg-transparent"
													onClick={() => {
														setSelectedIntegration(integration)
														setLearnMoreOpen(true)
													}}
												>
													Learn More
												</Button>
											</div>
										</div>
									</Card>
								)
							})}
						</div>
					) : (
						<Card className="p-8 text-center">
							<Search className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
							<p className="text-sm text-muted-foreground">No integrations match your search</p>
							<p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
						</Card>
					)}

					<Dialog open={learnMoreOpen} onOpenChange={setLearnMoreOpen}>
						<DialogContent className="max-w-2xl">
							<DialogHeader>
								<DialogTitle className="flex items-center gap-3">
									{selectedIntegration && (
										<>
											<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
												<img
													src={selectedIntegration?.icon_url}
													alt={`${selectedIntegration.name} icon`}
													className="h-6 w-6"
												/>
											</div>
											{selectedIntegration.name}
										</>
									)}
								</DialogTitle>
								<DialogDescription>{selectedIntegration?.description}</DialogDescription>
							</DialogHeader>
							<div className="py-4">
								<h4 className="font-medium mb-3">Setup Instructions</h4>
								<div className="space-y-2 text-sm text-muted-foreground whitespace-pre-line">
									{selectedIntegration?.setupInstructions}
								</div>
							</div>
							<DialogFooter>
								<Button variant="outline" onClick={() => setLearnMoreOpen(false)}>
									Close
								</Button>
								<Button
									onClick={() => {
										if (selectedIntegration) {
											const isActive = integrations.find((i) => i.id === selectedIntegration.id)?.enabled
											if (!isActive) {
												toggleIntegration(selectedIntegration.id)
											}
										}
										setLearnMoreOpen(false)
									}}
								>
									Add Integration
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</div>
			)}
		</div>
	)
}
Integrations.layout = (page: React.ReactNode) => <OrgLayout activeSection="integrations">{page}</OrgLayout>
export default Integrations
